import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Constants } from '../lib/const/constants';
import getCurrencySymbol from '../lib/currenyUtils';

function DashboardTransaction({ onDocumentsLoaded }) {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const getData = async () => {
    const login_id = sessionStorage.getItem('login_id');
    const login_token = sessionStorage.getItem('login_token');
    const login_workspaces_id = sessionStorage.getItem('login_workspaces_id');

    if (!login_id || !login_token) {
      sessionStorage.clear();
      navigate('/business');
      return;
    }

    setLoading(true);

    const from =
      new Date().getFullYear() +
      '-' +
      (new Date().getMonth() + 1) +
      '-' +
      (new Date().getDate() - 100);
    const to =
      new Date().getFullYear() +
      '-' +
      (new Date().getMonth() + 1) +
      '-' +
      new Date().getDate();

    try {
      const geturl =
        `${Constants.BASE_URL}API-FX-180-CONVERSIONLIST/${login_workspaces_id}` +
        `?page=0&from=${from}&to=${to}&transactionId=&date=&sender=&sendingAmount=&receivingAmount=&status=`;

      const response = await axios.get(geturl, {
        headers: {
          Authorization: 'Bearer ' + JSON.parse(login_token),
          fx_key: Constants.SUBSCRIPTION_KEY,
        },
      });

      const newTransactions = response.data.transactions || [];
      const otherTransactions = response.data.otherTransactions || [];
      const combined = [...newTransactions, ...otherTransactions];

      sessionStorage.setItem('transactionList', JSON.stringify(combined));

      setTransactions(combined.slice(0, 6));

      if (onDocumentsLoaded) {
        onDocumentsLoaded(response.data.typeOfDocuments || []);
      }
    } catch (err) {
      console.error('DashboardTransaction getData error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  function numberWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  const getDisplayName = (beneficiary) => {
    const meta = JSON.parse(beneficiary.meta || '{}');
    const raw =
      beneficiary.type === 'debit'
        ? meta.second_beneficiary_name
        : meta.sender_name;

    if (!raw) {
      return beneficiary.type === 'credit'
        ? `To ${beneficiary.settled_currency}`
        : 'Unknown';
    }

    const sep = beneficiary.settled_currency === 'EUR' ? ';' : ';;';
    const parts = raw.split(sep);
    return parts[0]
      .toLowerCase()
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  const getSubtitle = (beneficiary) => {
    const d = new Date(beneficiary.created_at);
    const day = d.getDate();
    const month = d.toLocaleString('en-GB', { month: 'short' });
    const label = beneficiary.type === 'debit' ? 'Sent' : 'Added';
    return `${label} · ${day} ${month}`;
  };

  const getPrimaryAmount = (beneficiary) => {
    const meta = JSON.parse(beneficiary.meta || '{}');
    const clientSellAmount = meta.conversion_meta?.conversion_client_sell_amount;
    const clientSellCurrency = meta.conversion_meta?.conversion_sell_currency;

    if (clientSellAmount && clientSellCurrency) {
      return `${numberWithCommas(parseFloat(clientSellAmount).toFixed(2))} ${clientSellCurrency}`;
    }
    return `${numberWithCommas(parseFloat(beneficiary.amount).toFixed(2))} ${beneficiary.settled_currency}`;
  };

  const getSecondaryAmount = (beneficiary) => {
    const symbol = getCurrencySymbol(beneficiary.settled_currency);
    return `${symbol}${numberWithCommas(parseFloat(beneficiary.amount).toFixed(2))}`;
  };

  const isCredit = (b) => b.type === 'credit';

  const SkeletonRow = () => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 0', borderBottom: '1px solid #f3f4f6',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#f0f0f0', flexShrink: 0 }} />
        <div>
          <div style={{ width: 130, height: 12, borderRadius: 6, background: '#f0f0f0', marginBottom: 6 }} />
          <div style={{ width: 80, height: 10, borderRadius: 6, background: '#f5f5f5' }} />
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ width: 90, height: 12, borderRadius: 6, background: '#f0f0f0', marginBottom: 6, marginLeft: 'auto' }} />
        <div style={{ width: 60, height: 10, borderRadius: 6, background: '#f5f5f5', marginLeft: 'auto' }} />
      </div>
    </div>
  );

  return (
    <div
      className="
        h-[599px]
        p-6
        rounded-2xl
        flex flex-col gap-6
        box-border
        overflow-hidden
        bg-[rgba(255,255,255,0.7)]
        border border-[#E5E7EB]
        border-t border-t-[#E5E7EB80]
        shadow-[0px_4px_6px_-4px_rgba(229,231,235,0.5),0px_10px_15px_-3px_rgba(229,231,235,0.5)]
      "
    >
      {(loading || transactions.length === 0) && (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 24, fontWeight: 600, color: '#1D2338' }}>
          Recent Transactions
        </span>
        <button
          onClick={() => navigate('/transactions')}
          style={{
            background: 'none', border: 'none',
            color: '#FF6B35', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', padding: 0,
          }}
        >
          See all →
        </button>
      </div>)}

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
        ) : transactions.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 0,
          }}>
            <img
              src="/pana.png"
              alt="No transactions"
              style={{ width: 220, height: 'auto', marginBottom: 20, objectFit: 'contain' }}
            />
            <p style={{
              fontSize: 20,
              fontWeight: 700,
              color: '#101828',
              margin: '0 0 10px 0',
              textAlign: 'center',
            }}>
              Lets get you started!
            </p>
            <p style={{
              fontSize: 15,
              fontWeight: 400,
              color: '#6A7282',
              margin: '0 0 28px 0',
              textAlign: 'center',
              maxWidth: 320,
              lineHeight: 1.6,
            }}>
              Begin managing your international payments quickly and securely from one place.
            </p>
            <button
              onClick={() => navigate('/transactions')}
              style={{
                background: '#D26521',
                color: '#ffffff',
                border: 'none',
                borderRadius: 10,
                padding: '16px 48px',
                fontSize: 18,
                fontWeight: 700,
                cursor: 'pointer',
                width: '100%',
                maxWidth: 498,
              }}
            >
              Make your first transaction
            </button>
          </div>
        ) : (
          transactions.map((tx, i) => {
            const credit = isCredit(tx);
            const name = getDisplayName(tx);
            const sub = getSubtitle(tx);
            const primary = getPrimaryAmount(tx);
            const secondary = getSecondaryAmount(tx);

            return (
              <div
                key={tx.id}
                onClick={() => navigate('/transactions')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 0',
                  borderBottom: i < transactions.length - 1 ? '1px solid #f3f4f6' : 'none',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div
                    className={`
                      w-12 h-12
                      rounded-[14px]
                      px-[13px]
                      flex items-center justify-center
                      flex-shrink-0
                      border
                      shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.1),0px_1px_3px_rgba(0,0,0,0.1)]
                      ${
                        credit
                          ? 'bg-gradient-to-br from-[#F0FDF4] to-[rgba(220,252,231,0.5)] border-t border-t-[#B9F8CF80]'
                          : 'bg-[#E7630D1A] border-t border-t-[#FFC9C980]'
                      }
                    `}
                  >
                    {credit ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 5v14M5 12h14"
                          stroke="#00A63E"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M7 17L17 7M17 7H8M17 7v9"
                          stroke="#D26521"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>

                  <div>
                    <div style={{
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#101828',
                      marginBottom: 3,
                      lineHeight: 1.3,
                    }}>
                      {name}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 400, color: '#6A7282' }}>
                      {sub}
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: credit ? '#00A63E' : '#101828',
                    marginBottom: 3,
                  }}>
                    {credit ? `+ ${primary}` : primary}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 400, color: '#6A7282' }}>
                    {secondary}
                  </div>
                </div>

              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

DashboardTransaction.propTypes = {
  onDocumentsLoaded: PropTypes.func,
};

export default DashboardTransaction;